import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3, BookOpen, CalendarDays, Cloud, Database, FileText, FolderOpen, GitBranch, Globe,
  KanbanSquare, Mail, MessagesSquare, Rocket, Settings, ShieldCheck, Ticket, Users, Video,
} from 'lucide-react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { SearchBox } from './registerFilters';
import { useRegisterDirty, useNavGuardConfirm } from '../unsavedChangesGuard';
import { useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { selectableProjects, projectOptionLabel } from './ProjectRegisters';

const BLANK_LINK_FILTERS = { text: '' };

// The fixed set an IconPicker choice comes from -- `key` is what's stored on ProjectLink.icon.
// Exported so CustomerProjectLinks.js's tiles resolve the same icon rather than growing their own copy.
export const ICON_OPTIONS = [
  { key: 'cloud', label: 'Cloud / SharePoint', Icon: Cloud },
  { key: 'git', label: 'Git / GitHub', Icon: GitBranch },
  { key: 'kanban', label: 'Board (ADO / Jira / Trello)', Icon: KanbanSquare },
  { key: 'chat', label: 'Chat (Teams / Slack)', Icon: MessagesSquare },
  { key: 'video', label: 'Video call', Icon: Video },
  { key: 'database', label: 'Database', Icon: Database },
  { key: 'book', label: 'Docs / Wiki', Icon: BookOpen },
  { key: 'chart', label: 'Dashboard / Report', Icon: BarChart3 },
  { key: 'mail', label: 'Mail', Icon: Mail },
  { key: 'ticket', label: 'Tickets / Support', Icon: Ticket },
  { key: 'rocket', label: 'App / Portal', Icon: Rocket },
  { key: 'folder', label: 'Folder / File share', Icon: FolderOpen },
  { key: 'calendar', label: 'Calendar', Icon: CalendarDays },
  { key: 'users', label: 'Team / Contacts', Icon: Users },
  { key: 'file', label: 'Document', Icon: FileText },
  { key: 'shield', label: 'Security', Icon: ShieldCheck },
  { key: 'settings', label: 'Settings / Admin', Icon: Settings },
  { key: 'globe', label: 'Generic / Other', Icon: Globe },
];
const ICON_BY_KEY = Object.fromEntries(ICON_OPTIONS.map((o) => [o.key, o.Icon]));

// Falls back to a keyword guess against label+URL when nobody has picked one -- most-specific
// pattern first. Keeps existing/unpicked links showing something meaningful instead of all
// defaulting to the same globe.
const GUESS_RULES = [
  [/sharepoint/, 'cloud'],
  [/github|git\.|\bgit\b/, 'git'],
  [/azure|devops|dev\.azure|\bado\b/, 'kanban'],
  [/jira|trello|monday\.com|asana/, 'kanban'],
  [/teams|slack/, 'chat'],
  [/zoom|meet\.google|webex/, 'video'],
  [/database|\bsql\b/, 'database'],
  [/docs\.google|confluence|wiki|notion/, 'book'],
  [/dashboard|report|powerbi|tableau/, 'chart'],
  [/outlook|mail\.google|gmail/, 'mail'],
  [/ticket|helpdesk|support|zendesk/, 'ticket'],
  [/portal|environment|\bapp\b|application/, 'rocket'],
];

function guessIconKey(label, url) {
  const haystack = `${label || ''} ${url || ''}`.toLowerCase();
  const match = GUESS_RULES.find(([pattern]) => pattern.test(haystack));
  return match ? match[1] : 'globe';
}

/** The icon to render for a link: its picked key if valid, else a keyword guess. */
export function resolveLinkIcon(item) {
  return ICON_BY_KEY[item.icon] || ICON_BY_KEY[guessIconKey(item.label, item.url)];
}

/**
 * A small popover grid of icon choices, same shape as registerFilters.js's MultiSelect (button +
 * absolutely-positioned panel, closes on outside click or Escape) but picking one icon instead of
 * toggling a checklist.
 */
function IconPicker({ value, label, url, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const CurrentIcon = resolveLinkIcon({ icon: value, label, url });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="icon-btn icon-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Change icon"
        style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}
      >
        <CurrentIcon size={16} strokeWidth={1.75} aria-hidden />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 30, marginTop: 4,
            display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, width: 216,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: '0 6px 18px rgba(0,0,0,0.12)', padding: 6,
          }}
        >
          {ICON_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              className="icon-btn"
              title={o.label}
              onClick={() => { onChange(o.key); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8,
                background: o.key === value ? 'rgba(47,147,177,0.15)' : 'none',
                color: o.key === value ? 'var(--primary-light)' : 'var(--text-mid)',
              }}
            >
              <o.Icon size={16} strokeWidth={1.75} aria-hidden />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function filterLinks(items, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  if (!needle) return items;
  return items.filter(
    (l) => lower(l.label).includes(needle) || lower(l.url).includes(needle) || lower(l.notes).includes(needle),
  );
}

const LINK_SORT_VALUE = {
  label: (r) => lower(r.label),
  url: (r) => lower(r.url),
};

const BLANK_LINK = () => ({ label: 'New link', url: 'https://example.com' });

function buildPayload(row) {
  return {
    label: row.label.trim(),
    url: row.url.trim(),
    notes: (row.notes || '').trim() || null,
    icon: row.icon || null,
    internalOnly: !!row.internalOnly,
  };
}

/**
 * A project's quick-links list (2026-09-17, reworked same day into a launch page) -- replaces
 * CustomerProjectLinks.js's hardcoded "coming soon" rows. Own standalone nav item/page rather than
 * a Project Registers tab (Jeremy) -- it's a link to somewhere else, not a log kept alongside the
 * others. Default view is a row of launch buttons (label + notes), not a grid -- this page exists
 * to be clicked through, not read as a log. "Edit Links" swaps to the familiar register-style grid
 * (same in-place editing as ProjectEventsRegister.js) to add/edit/remove links. `internalOnly` is
 * the same customer-visibility opt-out those use.
 */
export default function ProjectLinks() {
  const [projectId, setProjectId] = useSelectedProject();
  const [projects, setProjects] = useState([]);
  // Null until the coverage call answers -- same "don't claim a project has nothing logged until
  // we actually know" reasoning as ProjectRegisters.js's own `withItems`.
  const [withItems, setWithItems] = useState(null);
  const [showArchived, setShowArchived] = useState(!!projectId);
  const [mode, setMode] = useState('launch'); // 'launch' | 'edit'
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_LINK_FILTERS });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [sort, toggleSort] = useGridSort('label');
  const confirmNavigation = useNavGuardConfirm();

  useRegisterDirty(dirtyIds.size > 0);

  useEffect(() => {
    let cancelled = false;
    // allSettled: a failed coverage call must not cost us the project list -- the picker still
    // works with the ◇ marker simply absent, same reasoning as ProjectRegisters.js.
    Promise.allSettled([apiFetch('/projects'), apiFetch('/project-links/projects-with-items')])
      .then(([projectResult, coverageResult]) => {
        if (cancelled) return;
        if (projectResult.status === 'rejected') throw projectResult.reason;
        const rows = projectResult.value;
        setProjects(rows);
        setWithItems(coverageResult.status === 'fulfilled' ? new Set(coverageResult.value.projectIds) : null);
        const initial = selectableProjects(rows, !!projectId);
        if (!projectId && initial.length === 1) setProjectId(initial[0].id);
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleProjects = useMemo(() => selectableProjects(projects, showArchived), [projects, showArchived]);
  usePruneMissingProject(projectId, setProjectId, visibleProjects, projects.length > 0);

  const load = useCallback(() => {
    if (!projectId) { setItems([]); return; }
    setLoading(true);
    setError('');
    apiFetch(`/project-links?projectId=${projectId}`)
      .then((rows) => {
        setItems(rows);
        // Keeps the ◇ marker honest without a second round trip -- same approach as
        // ProjectRegisters.js's per-tab `noteCoverage`, just for the one list this page has.
        setWithItems((prev) => {
          if (!prev || prev.has(projectId) === (rows.length > 0)) return prev;
          const updated = new Set(prev);
          if (rows.length > 0) updated.add(projectId); else updated.delete(projectId);
          return updated;
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterLinks(items, filters), [items, filters]);
  const sorted = useMemo(
    () => sortRows(filtered, sort, (row, key) => LINK_SORT_VALUE[key](row)),
    [filtered, sort],
  );

  function editCell(id, key, value) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [key]: value } : i)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Add and delete reload the grid from the server, which would discard other rows' unsaved edits,
  // so save them first -- same as Project Budgets' Add Line.
  async function flushDirty(skipId) {
    const ids = [...dirtyIds].filter((id) => id !== skipId);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => apiFetch(`/project-links/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(items.find((r) => r.id === id))),
    })));
    setDirtyIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
  }

  async function addItem() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/project-links', { method: 'POST', body: JSON.stringify({ ...BLANK_LINK(), projectId }) });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteItem(id) {
    setActionError('');
    try {
      await flushDirty(id);
      await apiFetch(`/project-links/${id}`, { method: 'DELETE' });
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  function switchMode(next) {
    // Leaving the grid with unsaved edits behaves like any other nav-away from a dirty register.
    if (!confirmNavigation()) return;
    setMode(next);
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="page-eyebrow">Project Management</div>
          <h1 className="page-title">Project Links</h1>
          <div className="page-subtitle">
            Quick links to the other places a project lives — SharePoint, ADO, the customer's own
            environment. Shared with the customer on their portal unless marked internal.
          </div>
        </div>
        {projectId && (
          mode === 'launch' ? (
            <button className="btn btn-outline" onClick={() => switchMode('edit')} style={{ flexShrink: 0 }}>
              Edit Links
            </button>
          ) : (
            <button className="btn btn-outline" onClick={() => switchMode('launch')} style={{ flexShrink: 0 }}>
              ← Back to Links
            </button>
          )
        )}
      </div>

      <ErrorBanner error={error} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 280, maxWidth: 520 }}>
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                if (confirmNavigation()) setProjectId(e.target.value);
                else e.target.value = projectId;
              }}
              disabled={!visibleProjects.length}
            >
              <option value="">{visibleProjects.length ? 'Select a project…' : 'No projects yet'}</option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>{projectOptionLabel(p, withItems)}</option>
              ))}
            </select>
          </div>
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
              color: 'var(--text-mid)', cursor: 'pointer', paddingBottom: 10, whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                if (confirmNavigation()) setShowArchived(e.target.checked);
                else e.target.checked = showArchived;
              }}
            />
            Show archived projects
          </label>
        </div>
      </div>

      {!projectId ? (
        <div className="placeholder-page">
          <div className="placeholder-icon">🔗</div>
          <div className="placeholder-title">Select a project</div>
          <div className="placeholder-sub">Pick a project above to see or maintain its links.</div>
        </div>
      ) : loading ? (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      ) : mode === 'launch' ? (
        !items.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔗</div>
            <div className="placeholder-title">No links yet</div>
            <div className="placeholder-sub">Click "Edit Links" above to add the SharePoint site, ADO board, or anywhere else this project lives.</div>
          </div>
        ) : (
          <div className="link-tile-grid">
            {items.map((item) => {
              const Icon = resolveLinkIcon(item);
              return (
                <a key={item.id} href={item.url} target="_blank" rel="noreferrer" className="link-tile" title={item.notes || item.label}>
                  {item.internalOnly && <span className="badge badge-violet link-tile-badge">Internal</span>}
                  <div className="link-tile-icon"><Icon size={26} strokeWidth={1.6} aria-hidden /></div>
                  <div className="link-tile-label">{item.label}</div>
                  {item.notes && <div className="link-tile-notes">{item.notes}</div>}
                </a>
              );
            })}
          </div>
        )
      ) : (
        <>
          {actionError && <ErrorBanner error={actionError} />}

          <div className="card" style={{ marginBottom: 16 }}>
            <SearchBox value={filters.text} onChange={(text) => setFilters({ text })} placeholder="Label, URL or notes…" />
          </div>

          <div className="card">
            <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
              <div className="spacer" />
              <button className="btn btn-outline" onClick={addItem} disabled={!!busy}>
                {busy === 'adding' ? 'Adding…' : '+ Add link'}
              </button>
              <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
                {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
              </button>
            </div>
            {!items.length ? (
              <div className="placeholder-page">
                <div className="placeholder-icon">🔗</div>
                <div className="placeholder-title">No links yet</div>
                <div className="placeholder-sub">Add the SharePoint site, ADO board, or anywhere else this project lives.</div>
              </div>
            ) : !sorted.length ? (
              <div className="placeholder-page">
                <div className="placeholder-icon">🔍</div>
                <div className="placeholder-title">No links match this search</div>
                <div className="placeholder-sub">{items.length} logged against this project — clear the search to see them.</div>
              </div>
            ) : (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Icon</th>
                      <SortTh label="Label" sortKey="label" sort={sort} onSort={toggleSort} style={{ minWidth: 180 }} />
                      <SortTh label="URL" sortKey="url" sort={sort} onSort={toggleSort} style={{ minWidth: 220 }} />
                      <th style={{ minWidth: 200 }}>Notes</th>
                      <th style={{ width: 70 }}>Internal</th>
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((item, i) => (
                      <tr key={item.id} className={rowClassName(i, dirtyIds.has(item.id))}>
                        <td className="cell-check">
                          <IconPicker
                            value={item.icon}
                            label={item.label}
                            url={item.url}
                            onChange={(key) => editCell(item.id, 'icon', key)}
                          />
                        </td>
                        <td>
                          <div className="link-cell">
                            <input className="cell-input" value={item.label} onChange={(e) => editCell(item.id, 'label', e.target.value)} />
                            {item.url ? (
                              <a className="link-out" href={item.url} target="_blank" rel="noreferrer" title={`Open ${item.url} in a new tab`}>↗</a>
                            ) : null}
                          </div>
                        </td>
                        <td><input className="cell-input" value={item.url} onChange={(e) => editCell(item.id, 'url', e.target.value)} /></td>
                        <td><AutoGrowTextarea value={item.notes || ''} onChange={(e) => editCell(item.id, 'notes', e.target.value)} /></td>
                        <td className="cell-check">
                          <input
                            type="checkbox" checked={!!item.internalOnly} title="Hide this link from the customer portal"
                            onChange={(e) => editCell(item.id, 'internalOnly', e.target.checked)}
                          />
                        </td>
                        <td className="cell-check">
                          {confirmId === item.id ? (
                            <button className="icon-btn" onClick={() => { setConfirmId(''); deleteItem(item.id); }} title="Confirm delete — can't be undone">✓?</button>
                          ) : (
                            <button className="icon-btn" onClick={() => setConfirmId(item.id)} title="Delete this link">✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
